import React, { useEffect, useState, type ReactElement } from "react";
import { Label, RadioGroup, RadioGroupItem } from "@ndd/ui";
import type {
  RdScheduleActionRequestBody,
  RdScheduleActionScope,
  RdScheduleFeeDecision,
  RdScheduleOutcome,
} from "../../../../../shared/rd-schedule-actions";
import {
  OUTCOME_COPY,
  OUTCOME_ORDER,
  cancelConfirmLabel,
  feeReasonLabel,
  formatFee,
  isCancelOutcome,
  localParts,
  shortDateLabel,
  weekdayName,
  whenLabel,
} from "../../../lib/rd-schedule-actions";
import type { ActionAppointment, ActionDialogContext } from "./action-types";
import { ActionDialogFrame, ActionForm, OptionsStatus } from "./action-frame";
import { EmailCheckbox, ScopeFields } from "./action-parts";
import { useActionOptions, type ActionOptions } from "./use-action-options";
import { useActionSubmit, useScheduleApi } from "./use-action-submit";

// NO-876. Cancel or mark missed (spec § Appointment actions; canvas
// Cancel.dc.html): four outcomes, a Fee block for late cancel and no-show with
// the API's defaults, which visits, and an email. The confirm button names the
// outcome. A no-show only shows up after the start time.

export interface CancelDialogProps extends ActionDialogContext {
  appointment: ActionAppointment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
}

export function CancelDialog(props: CancelDialogProps): ReactElement {
  const { appointment, timeZone, open, onOpenChange } = props;
  const [pending, setPending] = useState(false);

  return (
    <ActionDialogFrame
      open={open}
      onOpenChange={onOpenChange}
      pending={pending}
      title="Cancel or mark missed"
      subtitle={`${appointment.clientName}${appointment.typeName ? `, ${appointment.typeName}` : ""}. ${whenLabel(appointment.startsAt, timeZone)}.`}
    >
      <CancelBody {...props} onPending={setPending} />
    </ActionDialogFrame>
  );
}

function CancelBody(
  props: CancelDialogProps & { onPending: (pending: boolean) => void }
): ReactElement {
  const api = useScheduleApi();
  const { state, reload } = useActionOptions(api, props.rdId, props.appointment.id);

  if (state.status !== "ready") {
    return (
      <OptionsStatus
        state={state}
        onRetry={reload}
        onClose={() => props.onOpenChange(false)}
      />
    );
  }

  return <CancelForm {...props} options={state.options} />;
}

export interface CancelDraft {
  outcome: RdScheduleOutcome;
  scope: RdScheduleActionScope;
  email: boolean;
  fee: Partial<Record<RdScheduleOutcome, RdScheduleFeeDecision>>;
}

export function feeApplies(
  outcome: RdScheduleOutcome
): outcome is "late_cancel" | "no_show" {
  return outcome === "late_cancel" || outcome === "no_show";
}

/** The fee choice the RD made, or the API's default for the outcome. */
export function feeDecisionFor(
  options: ActionOptions,
  draft: Pick<CancelDraft, "outcome" | "fee">
): RdScheduleFeeDecision | null {
  const { outcome } = draft;

  if (!feeApplies(outcome)) return null;

  const rule = outcome === "late_cancel" ? options.fee.lateCancel : options.fee.noShow;

  return draft.fee[outcome] ?? rule.defaultDecision;
}

export function buildCancelBody(
  appointmentId: string,
  options: ActionOptions,
  draft: CancelDraft
): RdScheduleActionRequestBody {
  const scope = options.series && isCancelOutcome(draft.outcome) ? draft.scope : "this";

  return {
    kind: "cancel",
    appointmentId,
    outcome: draft.outcome,
    scope,
    feeDecision: feeDecisionFor(options, draft),
    emailClient: draft.email,
  };
}

function CancelForm(
  props: CancelDialogProps & {
    options: ActionOptions;
    onPending: (pending: boolean) => void;
  }
): ReactElement {
  const { appointment, rdId, options, onChanged, onOpenChange, onPending } = props;
  const api = useScheduleApi();
  const [draft, setDraft] = useState<CancelDraft>({
    outcome: options.suggestedOutcome,
    scope: "this",
    email: true,
    fee: {},
  });
  const action = useActionSubmit({
    api,
    rdId,
    timeZone: props.timeZone,
    onPartial: onChanged,
    onSucceeded: () => {
      onChanged();
      onOpenChange(false);
    },
  });

  const change = (patch: Partial<CancelDraft>) => {
    action.clearProblem();
    setDraft((current) => ({ ...current, ...patch }));
  };

  useEffect(() => onPending(action.pending), [onPending, action.pending]);

  return (
    <ActionForm
      onSubmit={() =>
        void action.submit(buildCancelBody(appointment.id, options, draft))
      }
      problem={action.problem}
      back={{ label: "Keep appointment", onClick: () => onOpenChange(false) }}
      confirm={{
        label: cancelConfirmLabel(draft.outcome),
        variant: "destructive",
        ...action.confirmState,
        disabled: !options.outcomes[draft.outcome].available,
      }}
    >
      <CancelFields
        {...{ appointment, options, draft, change }}
        timeZone={props.timeZone}
        disabled={action.pending || action.locked}
      />
    </ActionForm>
  );
}

const UNAVAILABLE_REASON = {
  before_start: "Mark this after the start time.",
  after_start: "The visit has already started.",
} as const;

/** A no-show appears only once the visit has started. */
function shownOutcomes(options: ActionOptions): readonly RdScheduleOutcome[] {
  return OUTCOME_ORDER.filter(
    (outcome) =>
      !(outcome === "no_show" && options.outcomes.no_show.reason === "before_start")
  );
}

function OutcomeChoices(props: {
  appointment: ActionAppointment;
  options: ActionOptions;
  draft: CancelDraft;
  change: (patch: Partial<CancelDraft>) => void;
  disabled: boolean;
}): ReactElement {
  const { appointment, options, draft, change, disabled } = props;

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-2 text-body-lg font-semibold">What happened</legend>
      <RadioGroup
        aria-label="What happened"
        value={draft.outcome}
        disabled={disabled}
        onValueChange={(next) => change({ outcome: next as RdScheduleOutcome })}
      >
        {shownOutcomes(options).map((outcome) => {
          const state = options.outcomes[outcome];
          const copy = OUTCOME_COPY[outcome];
          const help = state.available
            ? copy.help(options.lateCancelWindowHours, appointment.firstName)
            : UNAVAILABLE_REASON[state.reason ?? "after_start"];

          return (
            // The whole card is the label, so its padding and swatch pick it too.
            <label
              key={outcome}
              htmlFor={`cancel-outcome-${outcome}`}
              className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-border px-3 py-2.5 has-[[data-state=checked]]:border-transparent has-[[data-state=checked]]:bg-primary-soft has-[[data-state=checked]]:shadow-(--button-selected) has-[:disabled]:cursor-not-allowed"
            >
              <RadioGroupItem
                id={`cancel-outcome-${outcome}`}
                value={outcome}
                disabled={!state.available}
                className="mt-0.5"
              />
              <span className="flex flex-col gap-0.5">
                <span className="font-semibold">
                  {copy.name(appointment.firstName)}
                </span>
                <span className="text-body text-muted-foreground">{help}</span>
              </span>
            </label>
          );
        })}
      </RadioGroup>
    </fieldset>
  );
}

function FeeBlock(props: {
  clientName: string;
  options: ActionOptions;
  draft: CancelDraft;
  change: (patch: Partial<CancelDraft>) => void;
  disabled: boolean;
}): ReactElement | null {
  const { clientName, options, draft, change, disabled } = props;
  const outcome = draft.outcome;

  if (!feeApplies(outcome)) return null;

  const isLate = outcome === "late_cancel";
  const rule = isLate ? options.fee.lateCancel : options.fee.noShow;
  const chargeAllowed = isLate ? true : options.fee.noShow.chargeAllowed;
  const decision = feeDecisionFor(options, draft) ?? rule.defaultDecision;
  const amount = formatFee(rule.amountCents);
  const kind = isLate ? "late-cancel" : "no-show";
  const set = (next: string) =>
    change({ fee: { ...draft.fee, [outcome]: next as RdScheduleFeeDecision } });

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-2 text-body-lg font-semibold">Fee</legend>
      <p className="text-body-lg text-muted-strong">
        <span className="font-semibold">{rule.allowed ? "Allowed." : "Charged."}</span>{" "}
        {feeReasonLabel(rule.reason, clientName)}
      </p>
      <RadioGroup
        aria-label="Fee"
        value={decision}
        disabled={disabled}
        onValueChange={set}
      >
        <FeeOption
          id="cancel-fee-charge"
          value="charge"
          disabled={!chargeAllowed}
          label={`Charge the ${kind} fee${amount ? `, ${amount}` : ""}`}
        />
        <FeeOption
          id="cancel-fee-waive"
          value="waive"
          label={isLate && rule.allowed ? "Don't charge (allowed)" : "Don't charge"}
        />
      </RadioGroup>
    </fieldset>
  );
}

function FeeOption(props: {
  id: string;
  value: string;
  label: string;
  disabled?: boolean;
}): ReactElement {
  return (
    <div className="flex items-center gap-2.5">
      <RadioGroupItem id={props.id} value={props.value} disabled={props.disabled} />
      <Label htmlFor={props.id} className="font-normal">
        {props.label}
      </Label>
    </div>
  );
}

export function CancelFields(props: {
  appointment: ActionAppointment;
  options: ActionOptions;
  draft: CancelDraft;
  change: (patch: Partial<CancelDraft>) => void;
  timeZone: string;
  disabled: boolean;
}): ReactElement {
  const { appointment, options, draft, change, timeZone, disabled } = props;
  const series = options.series;
  const original = localParts(appointment.startsAt, timeZone).date;
  const showScope = series !== null && isCancelOutcome(draft.outcome);

  return (
    <>
      <OutcomeChoices {...{ appointment, options, draft, change, disabled }} />
      <FeeBlock
        clientName={appointment.firstName}
        {...{ options, draft, change, disabled }}
      />
      {showScope ? (
        <ScopeFields
          idPrefix="cancel-scope"
          legend="Which visits"
          value={draft.scope}
          onChange={(scope) => change({ scope })}
          disabled={disabled}
          thisLabel={`Only ${shortDateLabel(original)}`}
          laterLabel={`This and every later ${weekdayName(original)} visit`}
          laterDisabledNote={
            series?.tooLong
              ? "This series has too many later visits to cancel at once."
              : undefined
          }
        />
      ) : null}
      <EmailCheckbox
        id="cancel-email"
        label={
          draft.outcome === "no_show"
            ? `Email ${appointment.firstName} that the visit was missed`
            : `Email ${appointment.firstName} that it's cancelled`
        }
        checked={draft.email}
        disabled={disabled}
        onChange={(email) => change({ email })}
      />
    </>
  );
}
